import { Vms } from '_lib/api';
import { useAppDispatch } from '_store';
import { pmvsSagas } from '_store/pmv/sagas';
import { enrolledVmsSelector } from '_store/pmv/selectors';
import { useLocalization } from '@fluent/react';
import { CardContent, IconButton, InputAdornment, TextField } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { usePmvL10n } from '../../../hooks/usePmvL10n';
import { useRolePermissions } from '../../../hooks/useRolePermissions';
import MessageDialog from '../../items/MessageDialog';
import CardListItem from './Card';
import styles from './styles';

export interface PmvLayoutProps {
  onUpdatePmv?: (idExpanded: string) => void;
}

const PmvListLayout: FC<PmvLayoutProps> = (crudProps) => {
  const { l10n } = useLocalization();
  const { getPmvString } = usePmvL10n();
  const classes = styles();
  const dispatch = useAppDispatch();
  const { canManagePmv, blockedActionMode } = useRolePermissions();

  const enrolledVms = useSelector(enrolledVmsSelector);
  const writeBlockedMessage = l10n.getString('forms-rbac-readonly-action-denied');

  const [filter, setFilter] = useState('');
  const [showMessage, setShowMessage] = useState({
    active: false,
    typeAction: 'delete',
    idToDelete: null as string | null,
    typeEvent: 'pmv',
  });

  useEffect(() => {
    dispatch(pmvsSagas.actions.vmsRequest());
  }, [dispatch]);

  const filterVms = (val: Vms) => {
    const searchVal = filter.toLowerCase();
    const name = (val.enrichment?.name ?? '').toLowerCase();
    return (
      name.includes(searchVal) ||
      val.id.toLowerCase().includes(searchVal) ||
      val.description.toLowerCase().includes(searchVal) ||
      val.roadNumber.toLowerCase().includes(searchVal)
    );
  };

  const onMessageLeaveConfirmed = (type: string) => {
    if (type === 'confirm' && showMessage.idToDelete) {
      dispatch(pmvsSagas.actions.deleteVms(showMessage.idToDelete));
    }

    setShowMessage({
      active: false,
      idToDelete: null,
      typeAction: 'delete',
      typeEvent: 'pmv',
    });
  };

  const onDeletePmv = (vmsId: string) => {
    setShowMessage({
      active: true,
      idToDelete: vmsId,
      typeAction: 'delete',
      typeEvent: 'pmv',
    });
  };

  return (
    <CardContent className={classes.cardContent}>
      {showMessage.active ? (
        <MessageDialog statusMessage={showMessage} onCloseDialog={onMessageLeaveConfirmed} />
      ) : null}

      <TextField
        className={classes.input}
        label={getPmvString('forms-pmv-search')}
        InputLabelProps={{ variant: 'standard' }}
        onChange={(ev) => setFilter(ev.target.value)}
        fullWidth
        type="text"
        value={filter}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton edge="start">
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: filter && (
            <IconButton aria-label="clear search" onClick={() => setFilter('')}>
              <CloseIcon />
            </IconButton>
          ),
        }}
      />

      <div style={{ overflow: 'auto' }}>
        {enrolledVms.filter(filterVms).map((item) => (
          <CardListItem
            asCard
            key={item.id}
            vms={item}
            onUpdate={() => crudProps.onUpdatePmv?.(item.id)}
            onDelete={() => onDeletePmv(item.id)}
            writeAllowed={canManagePmv}
            blockedActionMode={blockedActionMode}
            blockedMessage={writeBlockedMessage}
          />
        ))}
      </div>
    </CardContent>
  );
};

export default React.memo(PmvListLayout);
